import { Search } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { setSearchParam } from "../operations/filters";

const SEARCH_DEBOUNCE_MS = 300;

export interface FinanceListToolbarProps<T extends string> {
  views: readonly T[];
  view: T;
  /** Translation prefix of the view labels, e.g. `crm.finance.charges.views` */
  labelPrefix: string;
  /** Right side: create / generate buttons */
  actions?: ReactNode;
}

/** Quick filters and search of a finance list, both kept in the URL */
export const FinanceListToolbar = <T extends string>({
  views,
  view,
  labelPrefix,
  actions,
}: FinanceListToolbarProps<T>) => {
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const set = (key: string, value: string | null) =>
    setSearchParams((params) => setSearchParam(params, key, value), {
      replace: true,
    });

  // The search hits the server: wait for a pause in typing
  const [search, setSearch] = useState(q);
  useEffect(() => {
    if (search === q) return;
    const timeout = setTimeout(() => set("q", search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
      <div
        className="flex flex-wrap gap-1"
        role="group"
        aria-label={translate("crm.finance.filters")}
      >
        {views.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => set("view", item === views[0] ? null : item)}
            aria-pressed={item === view}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              item === view
                ? "border-primary bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {translate(`${labelPrefix}.${item}`)}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1 md:w-56 md:flex-none">
          <Search className="absolute top-1/2 left-2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={translate("crm.finance.search")}
            aria-label={translate("crm.finance.search")}
            className="pl-8"
          />
        </div>
        {actions}
      </div>
    </div>
  );
};
